import { useState, useCallback, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { UserMenu, User } from './UserMenu';
import { useStore } from '../../store';
import { LanguageSelector } from '../common/LanguageSelector';
import { useTranslation } from '../../hooks/useTranslation';

interface NavigationProps {
  activeDictionary?: string;
  user?: User | null;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  onLogout?: () => void;
  onNavigate?: () => void;
}

export function Navigation({
  activeDictionary = 'ODS',
  user = null,
  isAuthenticated: deprecatedIsAuth,
  isAdmin: deprecatedIsAdmin,
  onLogout,
  onNavigate,
}: NavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  
  // Store
  const activeDictionaryStore = useStore((state) => state.activeDictionary);

  const isAuthenticated = user !== null || deprecatedIsAuth === true;
  const isAdmin = user?.role === 'admin' || deprecatedIsAdmin === true;
  const isFreeUser = !user || user.subscriptionType === 'free';

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const handleNavClick = useCallback(() => {
    setIsMobileMenuOpen(false);
    onNavigate?.();
  }, [onNavigate]);

  const handleLogout = useCallback(() => {
    setIsMobileMenuOpen(false);
    onLogout?.();
  }, [onLogout]);

  const getNavLinkClasses = (isActive: boolean, isMobile = false) => {
    const base = isMobile
      ? 'min-h-touch flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-150'
      : 'px-4 py-2 rounded-lg font-medium transition-all duration-150';
    return isActive
      ? `${base} bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300`
      : `${base} text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-100`;
  };

  return (
    <>
      <nav className="hidden md:flex items-center gap-2" aria-label={t('a11y.mainNavigation')}>
        {/* Dictionary indicator - only show for authenticated users */}
        {isAuthenticated && (
          <div 
            className="mr-2 px-3 py-1.5 bg-accent-100 dark:bg-accent-900/40 text-accent-700 dark:text-accent-300 rounded-full text-sm font-medium flex items-center gap-1.5"
            title={`${t('nav.activeDictionary')}: ${activeDictionaryStore?.name || activeDictionary}`}
            role="status"
          >
            <span className="text-xs">📖</span>
            <span>{activeDictionaryStore?.name || activeDictionary}</span>
          </div>
        )}
        <NavLink to="/" onClick={handleNavClick} className={({ isActive }) => getNavLinkClasses(isActive)}>
          {t('nav.search')}
        </NavLink>
        {/* Language Selector - visible on all public pages */}
        <LanguageSelector variant="header" />
        <UserMenu user={user} onLogout={handleLogout} />
      </nav>

      <div className="flex md:hidden items-center gap-2">
        {isFreeUser && (
          <NavLink
            to="/subscription"
            className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-accent-500 to-accent-600 rounded-md shadow-sm"
            onClick={handleNavClick}
          >
            <SparkleIcon className="w-3.5 h-3.5" />
            Premium
          </NavLink>
        )}
        <button
          type="button"
          onClick={toggleMobileMenu}
          className="flex items-center justify-center min-h-touch min-w-touch rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors feedback"
          aria-label={isMobileMenuOpen ? t('a11y.menuClose') : t('a11y.menuOpen')}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div 
          className="fixed top-16 left-0 right-0 bottom-0 z-50 md:hidden bg-black/50"
          onClick={handleNavClick} 
          aria-hidden="true" 
        />
      )}

      <nav
        id="mobile-navigation"
        className={`fixed top-16 right-0 bottom-0 w-[280px] z-50 bg-white dark:bg-slate-800 shadow-2xl border-l border-slate-200 dark:border-slate-700 transform transition-transform duration-300 md:hidden ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        aria-label={t('a11y.mobileNavigation')}
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="flex flex-col h-full bg-white dark:bg-slate-800">
          {user && (
            <div className="px-4 py-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 flex items-center justify-center text-lg font-medium">
                  {user.email.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{user.email}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {user.role === 'admin' ? t('userMenu.admin') :
                     user.subscriptionType === 'premium' ? t('userMenu.premium') : t('userMenu.free')}
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Dictionary indicator - only show for authenticated users */}
          {isAuthenticated && (
            <div className="px-4 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
              <div className="inline-flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium shadow-sm border border-slate-300 dark:border-slate-600" role="status">
                <span>📖</span>
                <span>{t('nav.dictionary')}: <strong className="text-primary-600 dark:text-primary-400">{activeDictionaryStore?.name || activeDictionary}</strong></span>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex flex-col gap-2">
              <NavLink to="/" onClick={handleNavClick} className={({ isActive }) => getNavLinkClasses(isActive, true)}>
                <span className="text-lg">🔍</span><span>{t('nav.search')}</span>
              </NavLink>
              {isAuthenticated && (
                <>
                  <NavLink to="/account" onClick={handleNavClick} className={({ isActive }) => getNavLinkClasses(isActive, true)}>
                    <span className="text-lg">👤</span><span>{t('nav.account')}</span>
                  </NavLink>
                  <NavLink to="/subscription" onClick={handleNavClick} className={({ isActive }) => getNavLinkClasses(isActive, true)}>
                    <span className="text-lg">💳</span><span>{t('nav.subscription')}</span>
                  </NavLink>
                  {/* Dictionaries link - only for premium users and admins */}
                  {(user?.subscriptionType === 'premium' || user?.role === 'admin') && (
                    <NavLink to="/dictionaries" onClick={handleNavClick} className={({ isActive }) => getNavLinkClasses(isActive, true)}>
                      <span className="text-lg">📚</span><span>{t('nav.dictionaries')}</span>
                    </NavLink>
                  )}
                  {isAdmin && (
                    <NavLink to="/admin" onClick={handleNavClick} className={({ isActive }) => getNavLinkClasses(isActive, true)}>
                      <span className="text-lg">🛡️</span><span>{t('nav.admin')}</span>
                    </NavLink>
                  )}
                </>
              )}
              {!isAuthenticated && (
                <NavLink to="/login" onClick={handleNavClick} className={({ isActive }) => getNavLinkClasses(isActive, true)}>
                  <span className="text-lg">👤</span><span>{t('nav.login')}</span>
                </NavLink>
              )}
            </div>
          </div>
          <div className="p-4 border-t border-slate-100 dark:border-slate-700 space-y-2 overflow-visible">
            {/* Language Selector - mobile variant */}
            <LanguageSelector variant="mobile" />
            {isAuthenticated && (
              <button type="button" onClick={handleLogout} className="w-full min-h-touch flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <span>🚪</span><span>{t('nav.logout')}</span>
              </button>
            )}
            {!isAuthenticated && (
              <NavLink to="/register" onClick={handleNavClick} className="w-full min-h-touch flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors">
                <span>✨</span><span>{t('nav.registerFree')}</span>
              </NavLink>
            )}
            {isFreeUser && (
              <NavLink to="/subscription" onClick={handleNavClick} className="w-full min-h-touch flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-gradient-to-r from-accent-500 to-accent-600 text-white hover:from-accent-600 hover:to-accent-700 transition-colors">
                <SparkleIcon className="w-5 h-5" /><span>{t('nav.upgradePremium')}</span>
              </NavLink>
            )}
          </div>
        </div>
      </nav>
    </>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
    </svg>
  );
}
